// Register screen — paper-warm mobile-first per W2 §4.1.

import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { Button } from "@/components/ui/button";
import { ApiError, register } from "@/lib/api/auth";
import { useAuth } from "@/lib/auth/AuthProvider";

interface FieldErrors {
  email?: string;
  username?: string;
  password?: string;
  confirm?: string;
  display_name?: string;
  form?: string;
}

export function RegisterPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const next = params.get("next") || "/dashboard";
  const invitedParam = params.get("invited_by_code") || "";

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (password !== confirm) {
      setErrors({ confirm: "兩次密碼不一致" });
      return;
    }
    if (!email && !username) {
      setErrors({ form: "請填寫電子郵件或使用者名稱" });
      return;
    }
    setBusy(true);
    try {
      const code = inviteCode.trim() || invitedParam;
      await register({
        email: email.trim() || null,
        username: username.trim() || null,
        password,
        display_name: displayName.trim(),
        invited_by_code: code || null,
      });
      await refresh();
      // Per W2 §4.1: a successful ``invited_by_code`` auto-redeem puts the
      // user into onboarding step 3 (first diary); without a valid code we
      // route through /onboarding which dispatches to step 2 (pair) or
      // dashboard.  Respect an explicit ``?next=`` only when it isn't the
      // generic dashboard fallback.
      const target = code ? "/onboarding" : next && next !== "/dashboard" ? next : "/onboarding";
      nav(target.startsWith("/") ? target : "/onboarding", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(mapRegisterError(err));
      } else {
        setErrors({ form: "註冊時發生錯誤, 請稍後再試" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="建立帳戶"
      subtitle="先講下你點稱呼"
      backHref="/login"
      // W46.2: this is the one screen that introduces the product, so it is
      // the one screen carrying the slogan. `/login` keeps the bare mark.
      showBrandLockup
      footer={
        <>
          已有帳戶?{" "}
          <Link
            to={`/login${next && next !== "/dashboard" ? `?next=${encodeURIComponent(next)}` : ""}`}
            className="text-you-strong font-medium"
            data-testid="link-login"
          >
            登入
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <FormErrorBanner message={errors.form} />
        <FormField
          name="display_name"
          label="顯示名稱"
          required
          autoComplete="nickname"
          minLength={1}
          maxLength={50}
          value={displayName}
          onChange={setDisplayName}
          invalid={Boolean(errors.display_name)}
          errorMessage={errors.display_name}
          autoFocus
          disabled={busy}
          placeholder="你點稱呼?"
        />
        <FormField
          name="email"
          label="電子郵件"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          invalid={Boolean(errors.email)}
          errorMessage={errors.email}
          disabled={busy}
          placeholder="you@example.com"
        />
        <FormField
          name="username"
          label="使用者名稱"
          hint="3-20 字元, 英數字和底線"
          autoComplete="username"
          minLength={3}
          maxLength={20}
          value={username}
          onChange={setUsername}
          invalid={Boolean(errors.username)}
          errorMessage={errors.username}
          disabled={busy}
          placeholder="(可選)"
        />
        <FormField
          name="password"
          label="密碼"
          type="password"
          required
          autoComplete="new-password"
          minLength={8}
          maxLength={128}
          hint="至少 8 個字元"
          value={password}
          onChange={setPassword}
          invalid={Boolean(errors.password)}
          errorMessage={errors.password}
          disabled={busy}
        />
        <FormField
          name="confirm"
          label="確認密碼"
          type="password"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          invalid={Boolean(errors.confirm)}
          errorMessage={errors.confirm}
          disabled={busy}
        />
        {/* Per W2 §4.1 spec: when arriving via `/invite/{code}`, surface an
            inline 6-digit invite code field so the user sees + confirms what
            they're redeeming.  Pre-fills from ``?invited_by_code=`` query
            param if present (e.g. landed from InviteLanding CTA). */}
        <FormField
          name="invited_by_code"
          label="6 位數邀請碼"
          hint={
            invitedParam
              ? "已從邀請連結帶入, 可手動修改。"
              : "如收到另一半的邀請連結, 請輸入 6 位數邀請碼。"
          }
          inputMode="numeric"
          maxLength={6}
          value={inviteCode || invitedParam}
          onChange={(v) => setInviteCode(v.replace(/\D/g, "").slice(0, 6))}
          disabled={busy}
          placeholder="123456"
          data-testid="invited-by-code-input"
        />
        <Button
          type="submit"
          variant="primary"
          disabled={busy}
          data-testid="submit-register"
          leadingIcon={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        >
          {busy ? "建立中…" : "建立帳戶"}
        </Button>
      </form>
    </AuthShell>
  );
}

function mapRegisterError(err: ApiError): FieldErrors {
  // Per W2 §3.2 #28 — dispatch on error.code for field routing.
  switch (err.code) {
    case "AUTH_EMAIL_TAKEN":
      return { email: "此電子郵件已被使用" };
    case "AUTH_USERNAME_TAKEN":
      return { username: "此使用者名稱已被使用" };
    case "AUTH_DUPLICATE":
      return { form: err.message };
    case "AUTH_PASSWORD_TOO_SHORT":
      return { password: "密碼至少需要 8 個字元" };
    case "VALIDATION_FAILED":
      return mapValidationErrors(err);
    default:
      return { form: err.message || "註冊失敗" };
  }
}

function mapValidationErrors(err: ApiError): FieldErrors {
  const out: FieldErrors = {};
  for (const d of err.details ?? []) {
    if (d.field === "email") out.email = d.message;
    else if (d.field === "username") out.username = d.message;
    else if (d.field === "password") out.password = d.message;
    else if (d.field === "display_name") out.display_name = d.message;
    else if (!out.form) out.form = d.message;
  }
  if (!Object.keys(out).length) out.form = err.message;
  return out;
}
